import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapContainer, TileLayer, Polyline, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Clock, Ruler, Navigation } from "lucide-react";
import { toast } from "sonner";

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function _fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function _today() {
  return new Date().toISOString().slice(0, 10);
}

export function DelivererRoutesTab({ delivererId }) {
  const { authAxios } = useAuth();
  const [date, setDate] = useState(_today());
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRoute = async (d) => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/tracking/route/${delivererId}?date=${d}`);
      setRouteData(res.data);
    } catch {
      toast.error("Erro ao carregar rota");
      setRouteData(null);
    }
    setLoading(false);
  };

  useEffect(() => { fetchRoute(date); }, [delivererId]); // eslint-disable-line react-hooks/exhaustive-deps

  const points = routeData?.points?.filter(p => p.lat && p.lng) || [];
  const polyline = points.map(p => [p.lat, p.lng]);
  const summary = routeData?.summary || {};
  const mapCenter = points.length > 0 ? [points[0].lat, points[0].lng] : [38.7169, -9.1399];

  const orderGroups = points.reduce((acc, p) => {
    if (p.order_id) {
      acc[p.order_id] = (acc[p.order_id] || 0) + 1;
    }
    return acc;
  }, {});
  const relatedOrders = Object.keys(orderGroups);

  return (
    <div className="space-y-4">
      {/* Date picker + load button */}
      <div className="flex items-center gap-3">
        <input
          type="date"
          value={date}
          max={_today()}
          onChange={e => setDate(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="route-date-picker"
        />
        <Button
          size="sm"
          onClick={() => fetchRoute(date)}
          disabled={loading}
          className="gap-2"
          data-testid="load-route-btn"
        >
          <Navigation className="w-4 h-4" />
          {loading ? "A carregar…" : "Ver rota"}
        </Button>
      </div>

      {/* Summary cards */}
      {routeData && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-3 text-center">
              <MapPin className="w-4 h-4 mx-auto mb-1 text-emerald-600" />
              <p className="text-xs text-muted-foreground">Pontos GPS</p>
              <p className="text-xl font-bold">{summary.total_points ?? 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Ruler className="w-4 h-4 mx-auto mb-1 text-blue-600" />
              <p className="text-xs text-muted-foreground">KM aprox.</p>
              <p className="text-xl font-bold">{(summary.distance_km ?? 0).toFixed(1)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Clock className="w-4 h-4 mx-auto mb-1 text-orange-500" />
              <p className="text-xs text-muted-foreground">Início</p>
              <p className="text-lg font-semibold">{_fmtTime(summary.first_point_at)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Clock className="w-4 h-4 mx-auto mb-1 text-gray-500" />
              <p className="text-xs text-muted-foreground">Fim</p>
              <p className="text-lg font-semibold">{_fmtTime(summary.last_point_at)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Related orders */}
      {relatedOrders.length > 0 && (
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Pedidos com GPS:</span>{" "}
          {relatedOrders.map(oid => (
            <span key={oid} className="inline-block bg-emerald-100 text-emerald-800 rounded px-2 py-0.5 mr-1 text-xs font-mono">
              #{oid.slice(-6).toUpperCase()} ({orderGroups[oid]} pts)
            </span>
          ))}
        </div>
      )}

      {/* Map */}
      {points.length > 0 ? (
        <div className="rounded-xl overflow-hidden border" style={{ height: 400 }} data-testid="route-map">
          <MapContainer center={mapCenter} zoom={13} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap contributors"
            />
            <Polyline positions={polyline} color="#059669" weight={3} opacity={0.85} />
            <Marker position={[points[0].lat, points[0].lng]}>
              <Popup>Início {_fmtTime(points[0].timestamp)}</Popup>
            </Marker>
            <Marker position={[points[points.length - 1].lat, points[points.length - 1].lng]}>
              <Popup>Fim {_fmtTime(points[points.length - 1].timestamp)}</Popup>
            </Marker>
          </MapContainer>
        </div>
      ) : (
        routeData && (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <MapPin className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Sem pontos GPS para {date}.</p>
              <p className="text-xs mt-1">O estafeta precisa de ter a app aberta para registar GPS.</p>
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
}
